import { reconciliationQueueEn } from './reconciliation-queue';

export const reconciliationEn = {
  queue: reconciliationQueueEn,
  statutoryPaymentAllocation: {
    title: 'Allocate tax payment',
    previewTitle: 'Tax payment balances',
    readOnly: 'Review the balance of each component.',
    choose: 'Enter the amount to settle for each component.',
    confirm: 'Review the amounts before reconciling.',
    loading: 'Loading balances...',
    applying: 'Reconciling payment...',
    success: 'Payment reconciled.',
    close: 'View reconciliation',
    payment: 'Payment {{count}} · {{amount}}',
    remaining: 'Outstanding: {{amount}}',
    document: 'Tax guide',
    PRINCIPAL: 'Principal',
    FINE: 'Fine',
    INTEREST: 'Interest',
    invalid: 'Allocate the full payment without exceeding any component balance.',
    changed: 'The balance changed. Refresh balances and review your amounts again.',
    permission: 'Accounting management and operations access are required to view these balances.',
    failed: 'Could not reconcile. Your amounts have been preserved.',
    cancel: 'Cancel',
    back: 'Back',
    continue: 'Review amounts',
    apply: 'Confirm and reconcile',
    retry: 'Try again',
    refresh: 'Refresh balances',
  },
  common: {
    cancel: 'Cancel',
    saving: 'Saving...',
    loading: 'Loading...',
    moreActions: 'More actions',
  },
  cancelPosted: {
    retry: 'Try again',
    close: 'Close',
  },
  /** App chrome: matches `appShell.routes.titles.reconciliationReview` (inbox → review trail). */
  reviewRoute: {
    pageTitle: 'Review reconciliation',
  },
  review: {
    footer: {
      transaction: 'Transaction',
      accounts: 'Accounts',
      adjustments: 'Adjustments',
      difference: 'Difference',
      partialPayment: 'Partial payment',
      partialRemaining: 'Remaining balance',
      reconcilePartial: 'Confirm reconciliation (partial)',
      back: 'Back',
      vendorAdvanceSemConta: 'Advance (no bill)',
    },
    vendorAdvanceHighlight: {
      title: 'Vendor advance',
      subtitle:
        'Adjustment advance — the amount will be reconciled to the vendor without an existing AP bill.',
      subtitleBill:
        'A vendor advance payables bill was created — verify the amount and confirm reconciliation.',
      fullAmountBadge: 'Full transaction amount',
      vendorLabel: 'Vendor',
      amountLabel: 'Amount',
      removeAria: 'Remove advance',
    },
    header: {
      statusReconciled: 'Reconciled',
      statusDifference: 'Difference',
      statusAwaitingPair: 'Awaiting pair',
    },
    txAnchor: {
      cardItemLabel: 'Card item',
      transactionLabel: 'Bank transaction',
      select: 'Select',
      balanced: 'Reconciled',
      matchedAmount: '= {{amount}}',
      missing: 'Short by',
      exceeds: 'Over by',
      linkedAccounts: 'Linked accounts',
      reconciliation: 'Reconciliation',
      vendor: 'Vendor',
      vendorAdvanceReceipt: 'Vendor advance receipt - {{vendorName}}',
      loading: 'Loading reconciliation details',
      selectBelow: 'Select below',
      adjustments: 'adjustments',
      linkedCountLabelOne: '1 account',
      linkedCountLabel: '{{count}} accounts',
      billFxInline: 'FX {{rate}}',
      exchangeInline: 'FX {{rate}}',
      adjustmentShort: 'adjustment',
    },
    embedded: {
      dueShort: 'Due',
      paidShort: 'Paid',
      scheduledShort: 'Sched.',
      issueShort: 'Issued',
      managerialShort: 'Mgr.',
      postedShort: 'Posted',
      effectiveShort: 'Eff.',
      dataShort: 'Date',
      openAccount: 'Open account',
      exchangeRate: 'FX {{rate}}',
    },
    reconciledLayout: {
      linkedAccountsTitle: 'Linked accounts',
      linkedAccountsMeta: '{{count}} accounts · subtotal {{subtotal}}',
      adjustmentsTitle: 'Adjustments',
      adjustmentsTotal: 'Total',
    },
    selectedSummary: {
      totalMatch: 'Total match:',
      expand: 'Expand selection ({{count}})',
      dialogTitle: 'Selected accounts and adjustments',
      dialogDescription: '{{count}} selected items. Remove items here to update the match.',
      removeItem: 'Remove {{name}}',
      removeAdjustment: 'Remove {{type}} adjustment',
      unconditionalDiscountRecovery:
        'This adjustment clears accounts receivable and keeps its accounting classification, but it does not reduce the PIS/COFINS base again. If the recorded revenue is incorrect, correct the invoice and its source document through the correction workflow.',
    },
  },
  workbench: {
    title: 'Reconciliation Workbench',
    subtitle: 'Select and link transactions with your accounts payable and make adjustments.',
    confirm: 'Confirm Reconciliation',
    total: 'Total',
    difference: 'Difference',
    balanced: 'Reconciliation balanced',
    notBalanced: 'Add adjustments or items to balance',
    origins: {
      title: 'Origins',
      description: 'Bank transactions and cards',
      empty: 'No transactions selected. Select items from the reconciliation list.',
    },
    targets: {
      title: 'Targets',
      description: 'Accounts payable, receivable and transfers',
      empty: 'No targets selected. Add accounts payable or receivable.',
    },
    matching: {
      title: 'Reconciliation',
      description: 'Summary and financial adjustments',
    },
    adjustments: {
      title: 'Financial Adjustments',
      add: 'Add Adjustment',
      empty: 'No adjustments added',
    },
    error: {
      notBalanced: 'The difference of $ {{difference}} must be zero to confirm',
      failed: 'Failed to confirm reconciliation',
    },
    success: {
      created: 'Reconciliation created successfully',
    },
    transactionTypes: {
      all: 'All',
      debit: 'Debit',
      credit: 'Credit',
      transfer: 'Transfer',
    },
    internalOp: {
      title: 'Internal operation',
      description: 'Reconcile as yield, rescue or application (no link to payables/receivables)',
      confirmButton: 'Reconcile as internal operation',
      success: 'Internal operation reconciled successfully',
    },
    internalTransfer: {
      title: 'Transfer between accounts',
      description: 'Link the two transactions as transfer between own accounts',
      confirmButton: 'Reconcile transfer',
      success: 'Transfer reconciled successfully',
      needPair: 'Select two transactions (one debit and one credit) with matching amounts',
    },
    targetTypes: {
      payable: 'Payable',
      payroll: 'Payroll',
      receivable: 'Receivable',
      transfer: 'Transfers',
    },
    filters: {
      all: 'All',
      payable: 'Payable',
      receivable: 'Receivable',
      transfer: 'Transfers',
    },
    form: {
      adjustmentType: 'Type',
      amount: 'Amount',
      amountPlaceholder: '0.00',
      description: 'Description',
      descriptionPlaceholder: 'Optional adjustment description',
      vendorIdFallbackPlaceholder: 'Taken from the selected bill',
    },
    labels: {
      dueDate: 'Due date',
      vendor: 'Vendor',
      account: 'Account',
      toReconcile: 'To Reconcile',
      partialPayment: 'Partial Payment',
    },
    vendorReceipt: {
      menu: 'Register vendor receipt (Balance)',
      title: 'Register vendor receipt (Balance)',
      description: 'Apply this bank credit against the vendor advance balance.',
      available: 'Available balance',
      receipt: 'Unapplied credit from this transaction',
      noBalanceWarning:
        'This receipt cannot be registered because the vendor has no available advance balance.',
      amount: 'Amount to apply',
      confirm: 'Confirm receipt',
      confirmation: 'Confirm to record the reduction in the vendor advance balance.',
      remaining: 'Remaining balance',
      errorCandidates: 'Could not load eligible vendors.',
      errorAmount:
        'Enter an amount above zero and no greater than the credit or available balance.',
      error: 'Could not register the vendor receipt.',
      success: 'Vendor receipt registered.',
      processingTitle: 'Registering receipt',
      processingDescription: 'Please wait while we update the reconciliation and vendor balance.',
      successTitle: 'Receipt registered',
      errorTitle: 'Could not complete',
      errorDescription: 'Review the error before trying again.',
      retry: 'Review and try again',
      close: 'Close',
      done: 'Done',
      defaultDescription: 'Vendor advance return',
      errors: {
        VENDOR_RECEIPT_REQUIRES_PENDING_CREDIT: 'This credit is no longer pending reconciliation.',
        VENDOR_RECEIPT_INSUFFICIENT_BALANCE: 'The vendor does not have enough available balance.',
        VENDOR_RECEIPT_AMOUNT_EXCEEDS_REMAINING_CREDIT:
          'The amount cannot exceed the unapplied bank credit.',
        VENDOR_RECEIPT_VENDOR_MISMATCH:
          'This credit was partially allocated to another vendor. Continue with the vendor from the first allocation.',
        VENDOR_RECEIPT_SOURCE_ALREADY_LINKED:
          'This credit belongs to another reconciliation and cannot receive another application.',
        VENDOR_RECEIPT_AMOUNT_INVALID: 'Enter a valid monetary amount.',
      },
    },
    notices: {
      createApBill: 'An accounts payable will be created',
    },
    transactionCard: {
      ariaLabel: 'Transaction {{description}} of {{amount}}',
      selectAria: 'Select transaction {{description}}',
      noDescription: 'No description',
      fields: {
        counterparty: 'Counterparty',
        document: 'Document',
        classification: 'Classification',
        paymentMethod: 'Method',
        mcc: 'MCC',
        installment: 'Installment',
        referenceCode: 'Reference code',
      },
    },
    transaction: {
      title: 'Bank Transaction',
      titleCard: 'Card Item',
      counterparty: 'Counterparty',
      amount: 'Amount',
      date: 'Transaction Date',
      status: 'Status',
      description: 'Description',
      noData: 'No data available',
      fromAccount: 'From',
      toAccount: 'To',
      sameAccount: 'same account',
    },
    loading: 'Loading workbench...',
    processing: 'Processing...',
    list: {
      originsEmpty: 'No bank transactions available',
      targetsEmpty: 'No accounts available',
      targetsEmptyCta: 'No accounts found. Click "Create new" to add one.',
      searchPlaceholder: 'Search by vendor or description...',
      createTarget: 'Create new account',
      createTargetPayable: 'Create Account Payable',
      createTargetReceivable: 'Create Account Receivable',
      createTargetTransfer: 'Create Transfer',
      targetFallbackTitle: 'Account',
      viewBillTooltip: 'View account',
      viewInNewTab: 'Open in new tab',
      createAdjustmentBill: 'Create an account for this adjustment',
      adjustmentsEmptyTitle: 'No adjustments added',
      adjustmentsEmptySubtitle: 'Use the "Add adjustment" button to include financial adjustments',
      showingOnly: 'Showing only',
    },
    quickCreate: {
      title: 'Create Account Payable',
      titlePayable: 'Create Account Payable',
      titleTarifa: 'Create Fee',
      titleReceivable: 'Create Account Receivable',
      titleTransfer: 'Create Transfer',
      billTypeLabel: 'Account payable type',
      billTypeNormal: 'Normal',
      billTypeFee: 'Fee',
      suggestedByAi: 'AI suggested',
      feeRequiresTransactionHelp: 'Fee requires a bank transaction to be selected.',
      description: 'Create an account payable quickly without leaving reconciliation',
      descriptionPayable: 'Create an account payable quickly without leaving reconciliation',
      descriptionTarifa: 'Create a fee quickly without leaving reconciliation',
      descriptionReceivable: 'Create an account receivable quickly without leaving reconciliation',
      descriptionTransfer: 'Create a transfer quickly without leaving reconciliation',
      create: 'Create Account Payable',
      createPayable: 'Create Account Payable',
      createTarifa: 'Create Fee',
      addAsDiscount: 'Add as discount',
      createReceivable: 'Create Account Receivable',
      createTransfer: 'Create Transfer',
      comingSoon: 'Coming soon',
      comingSoonDescription: 'This functionality will be available soon.',
      comingSoonMessage:
        'Quick creation of accounts receivable and transfers will be available soon.',
      creating: 'Creating...',
      success: 'Account payable created successfully',
      successFee: 'Fee created successfully',
      error: 'Error creating account payable. Check the data and try again.',
      titleVendorAdvance: 'Vendor advance',
      titleVendorAdvanceBulk: 'Vendor advance',
      descriptionVendorAdvance: 'Create a vendor advance bill linked to the bank transaction.',
      descriptionVendorAdvanceCard: 'Create a vendor advance bill linked to the card transaction.',
      descriptionVendorAdvanceBulk:
        'Create vendor advance bills linked to the selected card transactions.',
      successVendorAdvance: 'Vendor advance created successfully',
      successVendorAdvanceBulk_one: '{{count}} vendor advance created successfully',
      successVendorAdvanceBulk_other: '{{count}} vendor advances created successfully',
      errorVendorAdvance: 'Error creating vendor advance. Check the data and try again.',
      errorVendorAdvanceBulk_one: '{{count}} vendor advance failed',
      errorVendorAdvanceBulk_other: '{{count}} vendor advances failed',
      bulkSummary_one: '{{count}} transaction selected',
      bulkSummary_other: '{{count}} transactions selected',
      bulkTotalAmount: 'Total: {{amount}}',
      createVendorAdvance: 'Create vendor advance',
      createVendorAdvanceBulk: 'Create {{count}} vendor advances',
      descriptionPlaceholderVendorAdvance: 'E.g.: Advance for project…',
      descriptionPlaceholderVendorAdvanceBulk:
        'Optional — when empty, each transaction uses its merchant name',
      errors: {
        entityRequired: 'Select an entity',
        amountRequired: 'Enter a valid amount',
        dueDateRequired: 'Enter due date',
        descriptionRequired: 'Enter a description',
        vendorRequired: 'Select a vendor',
        feeRequiresTransaction: 'Fee requires a linked bank transaction.',
        originRequired: 'Transaction origin is missing',
        // Stable API codes, read by code. Nothing is saved on either.
        FEE_BILL_AMOUNT_EXCEEDS_MOVEMENT:
          'The fee is larger than the transaction. Enter an amount up to the transaction amount.',
        VENDOR_ADVANCE_AMOUNT_EXCEEDS_ORIGIN:
          'The advance is larger than the transaction. Enter an amount up to the transaction amount.',
      },
    },
    quickCreateTransfer: {
      originAccount: 'Origin account',
      originPlaceholder: 'Select the source account',
      destinationAccount: 'Destination account',
      destinationPlaceholder: 'Select the target account',
      dateLabel: 'Date',
      paymentMethod: 'Payment method',
      descriptionPlaceholder: 'E.g.: Transfer between accounts, Application...',
      success: 'Transfer created successfully',
      error: 'Error creating transfer. Check the data and try again.',
      errors: {
        originRequired: 'Select the origin account',
        destinationRequired: 'Select the destination account',
        sameAccount: 'Origin and destination accounts must be different',
        amountRequired: 'Enter a valid amount',
        dateRequired: 'Enter the transfer date',
        descriptionRequired: 'Enter a description',
      },
    },
    summary: {
      title: 'Reconciliation Summary',
      totalOrigins: 'Total Origins',
      totalAdjustments: 'Total Adjustments',
      totalTargets: 'Total Targets',
      difference: 'Difference',
      targetsLabel: 'Selected Accounts',
      partialOf: 'of {{full}}',
      selection: {
        origins: {
          one: '{{count}} origin selected',
          other: '{{count}} origins selected',
        },
        targets: {
          one: '{{count}} target selected',
          other: '{{count}} targets selected',
        },
        adjustments: {
          one: '{{count}} adjustment',
          other: '{{count}} adjustments',
        },
      },
    },
    actions: {
      addAdjustment: 'Add Adjustment',
      addAdjustmentTitle: 'Add Financial Adjustment',
      addAdjustmentDescription: 'Add financial adjustments to balance the reconciliation.',
      getSuggestions: 'Get Suggestions',
      searchBills: 'Search Accounts Payable',
      searchButton: 'Search',
      clearSelection: 'Clear Selection',
      confirm: 'Confirm Reconciliation',
      cancel: 'Cancel',
    },
    adjustmentTypes: {
      TARIFA: 'Fee',
      ADIANTAMENTO: 'Advance payment',
      DEVOLUCAO_ADIANTAMENTO: 'Vendor receipt (advance balance)',
      JUROS: 'Interest',
      MULTA: 'Penalty',
      DESCONTO: 'Discount',
      DESCONTO_INCONDICIONAL: 'Unconditional discount (accounting adjustment)',
      IOF: 'IOF',
      IRRF: 'IRRF',
      PIS: 'PIS',
      COFINS: 'COFINS',
      CSLL: 'CSLL',
      INSS: 'INSS',
      ISS: 'ISS',
      FX: 'Exchange Rate Variation',
      ARREDONDAMENTO: 'Rounding',
      IMPOSTOS: 'Withheld taxes',
    },
    messages: {
      differenceNotZero: 'Difference must be zero to confirm',
      partialNotBalanced: 'The allocated amount must match the transaction amount',
      partialRemaining: 'The difference of {{amount}} will remain open in accounts payable.',
      remainingToBalance: 'Missing {{amount}} to match bank',
      remainingToClose: 'Missing {{amount}} to close account',
      noItemsSelected: 'Select at least one origin and target item',
      confirmSuccess: 'Conciliation confirmed successfully',
      vendorRequiredForAdvance: 'Select a vendor for the advance payment adjustment',
      vendorAdvanceSemContaTitle: 'Advance to vendor (no bill)',
      vendorAdvanceSemContaHint:
        'Records the outflow as a vendor advance without linking to an accounts payable bill.',
      vendorAdvanceSemContaAdd: 'Add',
      vendorAdvanceSemContaDescriptionPlaceholder: 'Optional',
      missingSelection: 'Select at least one origin, one target, and balance the difference',
      unbalancedHelp: 'Adjust values to balance the reconciliation',
      glOffsetPendingHelp:
        'Configure the accounting account for the bank account or card before reconciling. Edit in Settings > Bank accounts and credit cards.',
      loadError: 'Failed to load items for conciliation',
      addAdjustmentError: 'Failed to add adjustment',
      removeAdjustmentError: 'Failed to remove adjustment',
      confirmError: 'Failed to confirm conciliation',
      apSettlementAdjustmentUnassigned:
        'Could not reconcile because the interest, penalty, discount, or rounding is not linked to a bill. When the group has more than one bill, or also a receipt, apply the adjustment on the matching bill and try again.',
      apSettlementAdjustmentNotPayable:
        'Could not reconcile because an adjustment is not part of the bill settlement. Remove the fee, withheld tax, or advance and try again. Nothing was paid.',
      partialPaymentReconciled:
        'Partial payment of {{amount}} reconciled. Remaining balance: {{remaining}}',
      sourceAlreadyLinked:
        'One or more transactions are already linked to another group. Edit or delete the existing group in the review queue.',
      glPostingWarningDescription:
        'The reconciliation was completed, but there was an issue with the GL posting.',
    },
    result: {
      successTitle: 'Reconciliation confirmed',
      successDescription: 'The transaction was successfully reconciled.',
      warningTitle: 'Reconciliation confirmed with warnings',
      warningDescription:
        'The reconciliation was recorded, but there were issues with the GL posting.',
      errorTitle: 'Reconciliation failed',
      errorDescription: 'Unable to reconcile the transaction.',
      nextTransaction: 'Next transaction',
      nextPending: 'Next pending',
      noMorePending: 'All pending items have been reviewed.',
      nextPendingError: 'Could not fetch the next pending transaction.',
      viewReconciliation: 'View reconciliation',
      backToList: 'Back to list',
      glPosting: {
        missingAccountTitle: 'System GL account not configured',
        missingAccountDescription:
          'Map the GL account for {{slotLabel}} or select an account below.',
        periodClosedTitle: 'Accounting period closed',
        periodClosedDescription: 'There is no open accounting period for the transaction date.',
        periodClosedHint: 'This item will remain pending on the closing checklist.',
        unknownTitle: 'GL posting failed',
        genericWarning: 'The journal posting could not be completed.',
        saveMappingAndPost: 'Save mapping and post',
        leavePending: 'Leave pending',
        openSettings: 'Open settings',
        accountLabel: 'GL account',
        accountPlaceholder: 'Select account',
        retrySuccess: 'GL posting completed.',
        historicalLedgerSkipped: 'Already booked in the imported history — no journal',
        retryError: 'Could not complete posting. Try again.',
        saving: 'Saving...',
      },
    },
    bulkCompletion: {
      title: 'Review complete!',
      reconciled: '{{count}} reconciled',
      ignored: '{{count}} ignored',
      skipped: '{{count}} skipped',
      glWarningsTitle: 'GL posting notices',
      close: 'Close',
    },
    paymentMismatch: {
      title: 'Partial payment: amount differs from the scheduled payment',
      titleConfirmed: 'Partial payment: payment already confirmed',
      descriptionBeforeApproval:
        'This bill has a scheduled {{method}} of {{amount}}. What do you want to do with the existing payment?',
      descriptionBeforeApprovalMany:
        'This bill has {{count}} scheduled payments, {{amount}} in total. What do you want to do with the existing payments?',
      descriptionApproved:
        'This bill has a scheduled {{method}} of {{amount}}. As the bill is already approved, the rest of the partial payment stays on the bill as another scheduled payment.',
      descriptionApprovedMany:
        'This bill has {{count}} scheduled payments, {{amount}} in total. As the bill is already approved, the rest of the partial payment stays on the bill as another scheduled payment.',
      descriptionConfirmed:
        'This bill already has a payment confirmed by the bank with a different amount. Check the transaction. To replace the payment, give a reason below.',
      existingTitle: 'Payments on this bill',
      existingTitleBill: 'Payments on {{bill}}',
      dueDate: 'due {{date}}',
      choiceTitle: 'What to do with the remaining {{amount}}?',
      leaveOpen: 'Leave {{amount}} open on the bill',
      leaveOpenDescription:
        'Cancels the existing payment. The rest stays open, with no scheduled payment.',
      scheduleRemainder: 'Schedule a new {{method}} of {{amount}}',
      scheduleRemainderDescription:
        'Cancels the existing payment and schedules the rest with the same method, account and due date.',
      simulationTitle: 'Result',
      simulationCancelled: 'Cancelled: {{method}} of {{amount}}',
      simulationPaidNow: 'Paid with this transaction',
      simulationLeftOpen: 'Stays open on the bill',
      simulationNewScheduled: 'New scheduled {{method}} · due {{date}}',
      heldByBankTitle: 'This payment is already at the bank',
      heldByBankDescription:
        'Alchemy ERP does not cancel payments at the bank. Cancel it at the bank before you confirm, so the vendor is not paid twice.',
      heldByBankConfirm: 'I confirm that I cancelled this payment at the bank',
      justificationLabel: 'Reason',
      justificationPlaceholder: 'Why replace the confirmed payment?',
      justificationTooShort: 'Write at least {{min}} characters.',
      methods: {
        PIX: 'Pix',
        BOLETO: 'boleto',
        TED: 'TED',
        OTHER: 'payment',
      },
      confirm: 'Confirm and reconcile',
      back: 'Back',
      noticeTitle: 'The payments on {{bill}} do not match this transaction',
      noticeDescription: 'Choose what to do with the payments on this bill and reconcile again.',
      noticeAction: 'Adjust payments on {{bill}}',
      billLabel: 'bill {{number}}',
      billFallback: 'this bill',
      paymentStatus: {
        SCHEDULED: 'Scheduled',
        PENDING: 'Pending',
        PROCESSING: 'Processing',
        PAID: 'Paid',
        OTHER: 'Open',
      },
      retryFailed: 'Could not finish the reconciliation. Try again.',
      refusals: {
        RECONCILIATION_PAYMENT_MISMATCH:
          'The bank amount does not match the payments on this bill. Choose what to do with the existing payments.',
        RECONCILIATION_PAYMENT_MISMATCH_STALE:
          'The bill changed since you opened this screen. Close and open the reconciliation again.',
        RECONCILIATION_PAYMENT_MISMATCH_JUSTIFICATION_REQUIRED:
          'This bill has a payment confirmed by the bank. Write a reason of at least 10 characters to replace it.',
        RECONCILIATION_PAYMENT_MISMATCH_BANK_CANCELLATION_REQUIRED:
          'This payment was sent to the bank. Cancel it at the bank, then confirm that you did.',
        RECONCILIATION_PAYMENT_MISMATCH_REMAINDER_INVALID:
          'An approved bill must schedule the remaining amount as a new payment.',
        RECONCILIATION_PAYMENT_MISMATCH_PAYMENT_POSTED:
          'This payment already has accounting entries and cannot be replaced here. Edit the payments on the bill to match the bank amount.',
        RECONCILIATION_PAYMENT_MISMATCH_SEND_IN_FLIGHT:
          'This payment is being sent to the bank right now. Wait until the send finishes, then reconcile again.',
        RECONCILIATION_PAYMENT_MISMATCH_PERMISSION_REQUIRED:
          'To replace a payment that is confirmed or held by the bank, you need the permission to cancel Accounts Payable payments. Ask someone who has it to finish this reconciliation. Nothing was changed.',
      },
      permissionRequired:
        'To replace a payment that is confirmed or held by the bank, you need the permission to cancel Accounts Payable payments. Ask someone who has it to finish this reconciliation.',
    },
    allocationModal: {
      title: 'Confirm allocation',
      description: 'Review the amounts allocated to each account before confirming.',
      billColumn: 'Account',
      originalAmount: 'Original amount',
      allocatedAmount: 'Allocated amount',
      total: 'Total allocated',
      originAmount: 'Transaction amount',
      difference: 'Difference',
      differenceOk: 'Amounts match',
      differenceError: 'Total allocated must equal the transaction amount',
      confirm: 'Confirm and reconcile',
      cancel: 'Cancel',
      adjustmentDistributionTitle: 'Fine/Interest distribution',
      discountDistributionTitle: 'Discount distribution',
      adjustmentTotalExpected: 'Total fines/interest/discount',
      adjustmentDifferenceError: 'Amounts must match the adjustment total (difference: {{amount}})',
    },
    confirmation: {
      title: 'Confirm Reconciliation',
      description:
        'This action will reconcile the selected items. This operation cannot be undone.',
      bankSection: 'Bank Transactions',
      apSection: 'Accounts Payable',
      adjustmentsSection: 'Adjustments',
      differenceLabel: 'Final difference:',
      back: 'Back',
      detailsToggle: 'View details',
      itemCount: {
        one: '{{count}} item',
        other: '{{count}} items',
      },
    },
    suggestions: {
      title: 'Conciliation Suggestions',
      loading: 'Finding best suggestions...',
      emptyTitle: 'No suggestions found',
      emptyDescription: 'We could not find any automatic matches for the selected transactions.',
      matches: 'Suggested Accounts Payable',
      count: 'found',
      adjustmentsTitle: 'Suggested Financial Adjustments',
      method: {
        RULES: 'Rules',
        AI: 'AI',
        HYBRID: 'Hybrid',
      },
      toggle: {
        showExplanations: 'Show explanations',
        hideExplanations: 'Hide explanations',
      },
      actions: {
        viewDetails: 'View details',
        accept: 'Accept',
        reject: 'Reject',
        apply: 'Apply',
      },
    },
  },
  matching: {
    loading: 'Loading suggestions...',
    loadError: 'Failed to load suggestions',
    noCandidates: 'No suggestions found',
    noCandidatesDescription: 'No automatic matches available for this transaction.',
    candidatesFound: '{{count}} suggestions found',
    candidatesFound_plural: '{{count}} suggestions found',
    lastUpdated: 'Updated at',
    originTransaction: 'Transaction',
    scoreBreakdown: 'Score Details',
    viewScoreDetails: 'View score details',
    noDetailedScore: 'Detailed score not available for this reconciliation.',
    select: 'Select',
    selected: 'Selected',
    amount: 'Amount',
    date: 'Date',
    vendor: 'Vendor',
    vendorAdvancement: 'Vendor advance',
    reimbursementBadge: 'Reimbursement',
    customer: 'Customer',
    account: 'Account',
    status: 'Status',
    description: 'Description',
    counterparty: 'Counterparty',
    storedCandidatesTitle: 'Match Suggestions',
    storedCandidatesDescription: 'Possible matches found automatically for this transaction',
    showAllAccounts: 'Show all',
    showSuggestions: 'Show suggestions',
    billTotalAmount: 'Bill total',
    billTotalAccountShort: 'Account total',
    paymentAmount: 'Payment amount',
    netPayableAmount: 'Net payable amount',
    grossAmountShort: 'Gross amount',
    payments: 'Payments',
    selectPayment: 'Select payment',
    paymentOrdinal: 'Payment {{n}}',
    // Reconciliation preview translations
    reconciliationSummary: 'Reconciliation Summary',
    totalOrigin: 'Transaction',
    totalTarget: 'Account',
    difference: 'Difference',
    valuesNotMatching: 'Values do not match',
    differenceNote: 'The difference will be ignored for this reconciliation',
    howToHandleDifference: 'How to handle the difference?',
    // Difference handling options (side-by-side buttons)
    adjust: 'Adjust',
    newRecord: 'New record',
    ignore: 'Ignore',
    // Expanded details for each option
    adjustmentDescription: 'Record the difference of',
    asAdjustment: 'as an adjustment',
    selectAdjustmentType: 'Select type',
    newRecordDescription: 'Create record for',
    selectRecordType: 'Select type',
    newRecordTypes: {
      bill: 'Bill (AP)',
      billDesc: 'Create a new accounts payable bill',
      ar: 'Invoice (AR)',
      arDesc: 'Create a new accounts receivable invoice',
      transfer: 'Transfer',
      transferDesc: 'Record as internal transfer',
    },
    ignoreDescription: 'The difference of',
    willBeIgnored: 'will not be recorded',
    selectDifferenceAction: 'Select how to handle the difference',
    addAdjustment: 'Add adjustment',
    adjustmentsTotal: 'Total adjustments',
    remainingToAllocate: 'Remaining: {{amount}}',
    overAllocated: 'Over-allocated: {{amount}}',
    // Legacy keys (kept for compatibility)
    newBill: 'New bill',
    newBillDescription: 'Create a bill for',
    billForExtraAmount: 'Extra amount paid will be recorded as a new bill',
    billForPendingAmount: 'Pending amount will be recorded as a new bill',
    addAsAdjustment: 'Add as adjustment',
    createNewBill: 'Create new bill',
    createBillForExtra: 'Create a bill for the extra amount',
    createBillForMissing: 'Create a bill for the missing amount',
    ignoreDifference: 'Ignore the difference',
    differenceWillBeIgnored: 'The difference will not be recorded',
    cancel: 'Cancel',
    confirming: 'Reconciling...',
    confirmReconciliation: 'Reconcile',
    reconciliationSuccess: 'Reconciliation completed successfully!',
    reconciliationError: 'Failed to reconcile. Please try again.',
    candidateType: {
      payment: 'Payment',
      bill: 'Bill',
      invoice: 'Invoice',
      cardStatement: 'Card Statement',
      transfer: 'Transfer',
    },
    confidence: 'Confidence',
    selectedBadge: 'SELECTED',
    factors: {
      amount: 'Amount',
      exactAmount: 'Exact amount',
      date: 'Date',
      counterparty: 'Vendor',
      paymentMethod: 'Payment Method',
      automated: 'Automated Match',
      description: 'Description',
      recurringBoost: 'Recurring bill',
      fastPath: 'Fast Path',
      referenceCode: 'Reference Code',
      adjustmentExplanation: 'Adjustments',
    },
    dateTypes: {
      dueDate: 'Due Date',
      issueDate: 'Issue Date',
      managerialDate: 'Managerial Date',
      competencyDate: 'Competency Date',
      reimbursementDueDate: 'Reimbursement due date',
      scheduledDate: 'Scheduled Date',
      paidDate: 'Payment Date',
      postedAt: 'Posted Date',
      effectiveAt: 'Effective Date',
      default: 'Date',
    },
  },
  inbox: {
    pageTitle: 'Bank reconciliation',
    // ALC-5154: marking bank lines as automatic sweeps (automatic investment or redemption).
    sweepRule: {
      action: 'Automatic sweep',
      tab: 'Automatic sweep',
      badge: 'Automatic sweep',
      unmark: 'Unmark',
      shortcutKey: 'A',
      dialog: {
        title: 'Mark as automatic sweep',
        editTitle: 'Edit rule',
        selectedLinesLabel: 'Selected lines',
        scopeLabel: 'What to mark',
        scopeLine: 'This line only',
        scopeLines_one: 'Only this {{count}} line',
        scopeLines_other: 'Only these {{count}} lines',
        scopeRule: 'This line and future ones on this account (create a rule)',
        scopeRuleMany: 'These lines and future ones on this account (create a rule)',
        scopeRuleMixed: 'The lines are on different accounts; a rule applies to one account only.',
        wordsLabel: 'Description contains',
        wordsInput: 'Add a word',
        wordsRemove: 'Remove {{word}}',
        wordsHint: 'Dates and numbers are ignored.',
        wordsRequired: 'Enter at least one word.',
        directionLabel: 'Direction',
        directionDebit: 'Money out',
        directionCredit: 'Money in',
        directionBoth: 'Both',
        counterpartyLabel: 'Counterparty',
        previewMarks_one: 'Marks {{count}} line on this account, from {{from}} to {{to}}.',
        previewMarks_other: 'Marks {{count}} lines on this account, from {{from}} to {{to}}.',
        previewEdit_one: 'With the change: marks {{marks}} line (+{{added}}), unmarks {{removed}}.',
        previewEdit_other:
          'With the change: marks {{marks}} lines (+{{added}}), unmarks {{removed}}.',
        previewEditNone_one:
          'The change leaves the marked lines as they are: {{marks}} stays marked.',
        previewEditNone_other:
          'The change leaves the marked lines as they are: {{marks}} stay marked.',
        previewExceptionsKept_one: '{{number}} line someone unmarked stays unmarked.',
        previewExceptionsKept_other: '{{number}} lines someone unmarked stay unmarked.',
        previewRetry: 'Try again',
        toMarkLinesLabel: 'Lines to mark',
        toReleaseLinesLabel: 'Lines to release',
        successEditRun: 'Rule saved. Marked: {{added}}, released: {{removed}}.',
        previewTurnOff_one: '{{number}} line marked by the rule counts in the cash flow again.',
        previewTurnOff_other: '{{number}} lines marked by the rule count in the cash flow again.',
        previewTurnOffNeutral_one: '{{number}} line marked by the rule is no longer marked.',
        previewTurnOffNeutral_other: '{{number}} lines marked by the rule are no longer marked.',
        previewTurnOffNone: 'No line is marked by this rule right now.',
        previewTurnOffKept: 'Manual marks and the lines you unmarked stay as they are.',
        previewKeptMarkedClosed_one: '{{number}} line stays marked: its month is closed.',
        previewKeptMarkedClosed_other: '{{number}} lines stay marked: their months are closed.',
        lineKeptMarked: 'Stays marked: month closed',
        viewLines: 'View lines',
        hideLines: 'Hide lines',
        linesLabel: 'Preview lines',
        reconciledTo: 'Reconciled to {{document}}',
        balanceQuestion: 'Does the bank balance include the invested amount?',
        balanceYes: 'Yes, the balance includes it',
        balanceNo: 'No, the balance leaves it out',
        suggestion:
          'Suggested: yes. In the {{month}} check, with these lines the gap is {{withLines}}; without them, {{withoutLines}}.',
        submitRule_one: 'Mark {{count}} line',
        submitRule_other: 'Mark {{count}} lines',
        submitLine: 'Mark line',
        submitRulePending: 'Mark lines',
        submitEdit: 'Save rule',
        successRule: 'Rule created and lines marked',
        successLine: 'Line marked',
        successLines: 'Lines marked',
        successEdit: 'Rule saved',
        counterpartyName: 'Counterparty name',
        counterpartyDocument: 'Counterparty document',
        counterpartyHint: 'When a document is given, the name is not used.',
        excluded_one:
          '{{count}} selected line is left out: card line, reconciled, ignored or already marked.',
        excluded_other:
          '{{count}} selected lines are left out: card lines, reconciled, ignored or already marked.',
        capExceeded: '{{count}} lines are selected; mark up to {{max}} at a time.',
        previewLoading: 'Working out the preview…',
        previewNone: 'No line on this account matches this rule.',
        previewSum: 'Sum of the lines to mark: {{sum}}',
        previewSkippedTotal_one: '{{count}} line skipped: {{reasons}}.',
        previewSkippedTotal_other: '{{count}} lines skipped: {{reasons}}.',
        skipReason: {
          RECONCILED_one: '{{count}} already reconciled to a document',
          RECONCILED_other: '{{count}} already reconciled to a document',
          IGNORED_one: '{{count}} ignored',
          IGNORED_other: '{{count}} ignored',
          CLOSED_MONTH_one: '{{count}} in a closed month',
          CLOSED_MONTH_other: '{{count}} in a closed month',
        },
        lineSkipReason: {
          RECONCILED: 'Reconciled',
          IGNORED: 'Ignored',
          CLOSED_MONTH: 'Closed month',
        },
        skippedLinesLabel: 'Skipped lines',
        heldBy: '{{type}} no. {{number}}',
        heldByNoNumber: '{{type}}',
        documentType: {
          AP_BILL: 'Bill',
          AP_PAYMENT: 'Bill payment',
          AR_INVOICE: 'Receivable invoice',
          AR_DEBIT_NOTE: 'Debit note',
          TRANSFER: 'Transfer',
          BANK_TRANSFER: 'Transfer',
          BANK_TX: 'Bank transaction',
          CARD_STATEMENT: 'Card statement',
          other: 'Document',
        },
        suggestionNo:
          'Suggested: no. In the {{month}} check, with these lines the gap is {{withLines}}; without them, {{withoutLines}}.',
        suggestionNeutral:
          'In the {{month}} check, with these lines the gap is {{withLines}}; without them, {{withoutLines}}.',
        balanceRequired: 'Say whether the bank balance includes the invested amount.',
        rulesLoading: 'Loading the rules of this account. Try again in a moment.',
        rulesFailed: 'The rules of this account could not be read.',
        flagFailed:
          'The lines were marked, but the account’s balance setting could not be changed ({{code}}). Change the option on the bank account.',
        flagClosedMonth:
          'The lines were marked, but the account’s balance setting did not change: the account has marked lines in closed months. Reopen those months and change the option on the bank account.',
        flagFailedLink: 'Open the bank account',
        close: 'Close',
        successRuleCount_one: 'Rule created and {{count}} line marked',
        successRuleCount_other: 'Rule created and {{count}} lines marked',
        successSkipped_one: '{{count}} line skipped.',
        successSkipped_other: '{{count}} lines skipped.',
        markSkipped: {
          RECONCILED: 'The line is already reconciled to a document.',
          IGNORED: 'The line is ignored. Undo the ignore before marking.',
          CLOSED_MONTH: 'The line’s month is closed.',
          ALREADY_MARKED: 'The line is already marked as automatic sweep.',
          NOT_FOUND: 'Line not found.',
        },
      },
      toggle: {
        turnOffTitle: 'Turn off the rule?',
        turnOffDescription:
          'It stops marking new lines and releases the lines it marked. See the effect below.',
        turnOffConfirm: 'Turn off',
        turnOffSuccess: 'Rule turned off',
        turnOnTitle: 'Turn on the rule?',
        turnOnDescription:
          "It marks the lines that match again, in the account's history and in the next imports.",
        turnOnConfirm: 'Turn on',
        turnOnSuccess: 'Rule turned on',
      },
      notice: {
        rule_one:
          'This account’s rule: description contains {{words}} · {{direction}} · {{count}} line',
        rule_other:
          'This account’s rule: description contains {{words}} · {{direction}} · {{count}} lines',
        directionDebit: 'money out only',
        directionCredit: 'money in only',
        directionBoth: 'both directions',
        manage: 'Manage rules',
        off: 'turned off',
        counterparty: 'counterparty {{name}}',
        loadError: 'Could not load this account’s rules.',
        retry: 'Try again',
        loading: 'Loading this account’s rules',
      },
      list: {
        originRule: 'Rule',
        originManual: 'Manual',
        markedBy: 'Marked by {{name}} on {{date}}',
        markedByImport: 'Marked by the automatic import on {{date}}',
        markedByImportNoDate: 'Marked by the automatic import',
        empty: 'No lines marked as automatic sweep in this period.',
        emptyHint: 'Mark a line in Analyzing: a rule can mark the account’s next lines.',
        footnote:
          'These lines stay out of the cash flow and are checked against the bank balance as the account setting says.',
        footnoteMixed:
          'Only the lines of {{accounts}} stay out of the cash flow: the balance of those accounts includes the investment. On the other accounts the lines stay in the cash flow as transfers to investments.',
      },
      unmarkSuccess: 'Line unmarked',
      unmarkSuccessRule: 'Line unmarked. The rule will not mark it again.',
      unmarkSkipped: {
        NOT_MARKED: 'The line is not marked.',
        CLOSED_MONTH: 'The line’s month is closed.',
        NOT_FOUND: 'Line not found.',
      },
      filterRule: 'Rule',
      errors: {
        generic: 'Could not complete the action. Try again.',
        SWEEP_SEED_NOT_FOUND: 'The rule’s source line was not found on this account.',
        SWEEP_PREVIEW_INVALID:
          'To preview an edit, a turn on or a turn off, name the rule. A new rule has no identifier.',
        SWEEP_RULE_UPDATE_INVALID:
          'Send either the rule’s new facts or its on/off state, not both and not neither.',
        SWEEP_RULE_DUPLICATE: 'This account already has an automatic sweep rule like this one.',
        SWEEP_RULE_NOT_FOUND: 'Automatic sweep rule not found.',
        SWEEP_RULE_WITHOUT_WORDS:
          'The rule needs at least one word that is not a number or a date.',
        SWEEP_RULE_COUNTERPARTY_INVALID:
          'The counterparty name needs at least one letter, and the document at least one digit.',
        SWEEP_ACCOUNT_NOT_FOUND: 'Bank account not found.',
        SWEEP_RULE_MIXED_ACCOUNTS:
          'A rule belongs to one bank account: the selected lines are on more than one.',
        SWEEP_SEED_DOES_NOT_MATCH:
          'The rule does not catch the marked line. Change the words, the direction or the counterparty.',
        SWEEP_FLAG_CLOSED_MONTH_MARKS:
          'This account has lines marked as automatic sweep in closed months. Reopen those months before you change whether the bank balance includes the invested amount.',
        RECON_V2_SWEEP_MARKED: 'This line is marked as automatic sweep. Unmark the line first.',
      },
      review: {
        markedHint: 'Marked as automatic sweep',
      },
    },
    acquirerReport: {
      action: 'Acquirer report',
      title: 'Import the acquirer report',
      description:
        'Approved captures in the report become the expected credit per batch, day and currency, cut at the rail’s cutoff hour. The engine matches the bank’s credits against them.',
      entity: 'Entity',
      entityPlaceholder: 'Select the entity',
      rail: 'Rail (acquirer)',
      railPlaceholder: 'Select the rail',
      noRails: 'No rail configured for this entity',
      cutoff: 'cutoff {{hour}}h UTC',
      files: 'Report CSV files',
      filesHint: 'A month usually ships as several files; upload them all at once.',
      confirm: 'Import',
      cancel: 'Cancel',
      close: 'Close',
      imported: '{{recorded}} batch(es) recorded, {{skipped}} already existed.',
      summary:
        '{{files}} file(s), {{rows}} rows: {{charges}} approved captures, {{refunds}} refunds (they do not net the batch).',
      batches: '{{batches}} batch(es) — {{recorded}} recorded, {{skipped}} already existed.',
    },
    extratosToggle: {
      expand: 'Expand',
      collapse: 'Collapse',
      ariaExpand: 'Expand the per-account and card statements panel',
      ariaCollapse: 'Collapse the statements panel',
    },
    extratosPanel: {
      legendReconciled: 'reconciled',
      legendSuggestion: 'suggestions',
      legendPending: 'pending',
      viewFull: 'View full statements →',
      empty: 'No accounts or cards with a statement in this period.',
      statementAt: 'statement @{{date}}',
      noStatementDate: 'no statement date',
      pendingShort: 'pend.',
      pendingVolumeShort: 'pend.',
      pendingVolumeHint: 'Includes analyzing and suggestions (not yet reconciled in this period).',
      reconciledShort: 'reconc.',
      selectedBadge: 'Selected',
      selectedBankScope: 'Selected account: {{name}}',
      selectedCardScope: 'Selected card: {{name}}',
      selectedTotal: '{{count}} items',
    },
    title: 'Bank Reconciliation',
    subtitle:
      'Reconcile bank, card, and investment transactions with accounts payable and receivable',
    viewMode: {
      standard: 'Cards',
      minimal: 'Table',
      aria: 'Toggle list layout',
      ariaLabel: 'View mode',
      cards: 'Cards',
      table: 'Table',
      cardsLabel: 'Card view (focus)',
      tableLabel: 'Table view (macro)',
    },
    minimalView: {
      childCard: 'Card',
      group: {
        bankStatement: 'Bank statement',
        linkedAccount: 'Linked account',
      },
      columns: {
        date: 'Date',
        description: 'Description',
        account: 'Account',
        value: 'Amount',
        link: 'Link',
        matchValue: 'Amount',
        paymentDate: 'Payment',
        dueDate: 'Due date',
        confidence: 'Confidence',
        actions: 'Actions',
      },
      matchOrigin: {
        ai: 'AI',
        rules: 'Rules',
        manual: 'Manual',
      },
      confidence: {
        high: 'High',
        medium: 'Medium',
        low: 'Low',
      },
      pending: {
        awaitingAi: 'Awaiting AI analysis',
      },
      accountType: {
        checking: 'Checking',
        savings: 'Savings',
        investment: 'Investment',
        card: 'Card',
        other: 'Account',
      },
      accountTooltip: {
        branch: 'Branch',
        account: 'Account',
        aria: 'Account: {{account}}',
      },
      rowActionsMenu: 'Transaction actions',
      transactionDetailsAria: 'Transaction details',
      rowDetails: {
        title: 'Transaction details',
        date: 'Date',
        account: 'Account',
        counterparty: 'Counterparty',
        description: 'Description',
        amount: 'Amount',
        type: 'Type',
        direction: 'Direction',
        debit: 'Debit',
        credit: 'Credit',
        paymentMethod: 'Payment method',
        document: 'Document',
        reference: 'Reference',
        foreignAmount: 'Foreign currency amount',
        installment: 'Installment',
        operationType: 'Operation (Open Finance)',
        providerCategory: 'Category (Open Finance)',
        providerSubCategory: 'Subcategory',
        cnae: 'CNAE',
        counterpartyBank: 'Counterparty bank',
      },
    },
    common: {
      close: 'Close',
    },
    details: {
      title: 'Edit transaction',
    },
    reconciliationDetails: {
      title: 'Reconciliation Details',
    },
    glPostingBanner: {
      title: 'GL posting pending',
      count: '{{count}} link(s) need attention',
      neverPosted: 'Posting was never attempted',
      retry: 'Retry',
      triggerPosting: 'Trigger posting',
      linkLabel: '{{targetType}} · {{amount}}',
      refreshError: 'Could not refresh reconciliation details.',
      expandDetails: 'Show details',
      collapseDetails: 'Hide details',
      collapseBanner: 'Collapse alert',
      expandBanner: 'Expand alert',
      collapseBannerAria: 'Collapse the pending GL posting alert',
      expandBannerAria: 'Expand the pending GL posting alert',
      relatedBillFailureContext: 'Bill link (rolled into payment row): {{reason}}',
      awaitingBillApprovalBanner:
        'GL posting pending — will be performed automatically when the linked bill is approved',
      awaitingBillApprovalRowHint:
        'Will be performed automatically when the linked bill is approved.',
    },
    review: {
      title: 'Review Reconciliation Match',
      titleReconciled: 'Reconciled Item',
      titleAwaitingPair: 'Awaiting matching leg',
      awaitingPair: {
        cardTitle: 'Awaiting matching leg',
        cardDescription:
          'This transfer was flagged as internal and the in-transit leg has already been posted to the general ledger. The pair will be linked automatically as soon as the matching transaction shows up in the statement.',
        cardHint:
          'In the meantime, the amount remains in "Cash in Transit" and no further action is required for this transaction.',
      },
      reconciled: {
        valuesColumn: 'Amount',
        financialAdjustments: 'Financial adjustments',
        datesColumn: 'Dates',
        glPostingFailedTitle: 'GL posting failed',
        glPostingFailedFallback: 'Unknown error during posting.',
        billFaceLabel: 'Amount in bill currency',
        cardChargeLabel: 'Card charge ({{currency}})',
        extraAdjustmentsBrl:
          'Additional adjustments in {{currency}} (they do not replace the foreign amount on the bill)',
        cardTotalEquals: 'The amount charged on the card is the {{currency}} total above',
      },
    },
    ignoredNoSuggestions:
      'Transaction is ignored. Click Undo ignored to return to analysis and see reconciliation options.',
    focusMode: {
      title: 'Focus mode',
      itemCount: '{{current}} of {{total}}',
      shortcutAddInterestAndReconcile: 'Add as interest and reconcile',
      shortcutAddFeeAndReconcile: 'Add as fee and reconcile',
      shortcutAddDiscountAndReconcile: 'Add as discount and reconcile',
      discountConfirmTitle: 'Confirm discount and reconcile',
      discountConfirmDescription:
        'This payment will be reconciled with the bill and a discount of {{amount}} will be applied, visible on accounts payable and posted to the general ledger.',
      discountConfirmAction: 'Apply discount and reconcile',
      shortcutReconcile: 'Reconcile',
      shortcutInternalOp: 'Reconcile as internal operation',
      shortcutIgnoreAndNext: 'Ignore and next',
      shortcutTransfer: 'Reconcile transfer',
      noPendingItems: 'No pending items to reconcile',
      shortcutsAria: 'Reconciliation shortcuts',
    },
    aria: {
      selectAll: 'Select all',
      selectItem: 'Select {{description}}',
      openMenu: 'Open menu',
      actions: 'Actions',
    },
    tabs: {
      pending: 'Analyzing',
      suggestions: 'Suggestions',
      matched: 'Matched',
      reconciled: 'Reconciled',
      ignored: 'Ignored',
      all: 'All',
    },
    summary: {
      pending: 'Analyzing',
      suggestions: 'Suggestions',
      matched: 'Matched',
      reconciled: 'Reconciled',
      ignored: 'Ignored',
      todos: 'All',
    },
    filters: {
      title: 'Filters',
      active: 'Active filters:',
      account: 'Account',
      accountPlaceholder: 'Select account',
      entity: 'Entity',
      entityAll: 'All entities',
      entityLoading: 'Loading entities',
      entityCount_one: '{{count}} entity',
      entityCount_other: '{{count}} entities',
      entityError: 'Could not load entities',
      description: 'Description',
      search: 'General search',
      document: 'Document',
      documentPlaceholder: 'CPF, CNPJ, or tax identifier',
      status: 'Status',
      statusPlaceholder: 'All',
      amount: 'Amount',
      amountPlaceholder: 'Amount',
      amountOp: {
        eq: 'Equals',
        gte: 'Greater or equal',
        lte: 'Less or equal',
      },
      transactionType: 'Transaction type',
      transactionTypePlaceholder: 'Select type',
      transactionTypeAll: 'All',
      transactionTypeDebit: 'Payables',
      transactionTypeCredit: 'Receivables',
      transactionTypeTransfer: 'Transfer between accounts',
      transactionTypeYield: 'Investment yields',
      transactionTypeInternalOp: 'Internal operations',
      transactionTypeFeeTax: 'Fees and taxes',
      transactionTypeEmployeePayment: 'Payroll payments',
      transactionTypeOther: 'Other',
      transferSubType: 'Transfer type',
      transferSubTypePlaceholder: 'Select type',
      transferSubTypes: {
        all: 'All',
        internal: 'Between own accounts',
        investment: 'Investments',
        pending: 'Awaiting match',
      },
      paymentMethod: 'Method',
      paymentMethodPlaceholder: 'Select method',
      paymentMethodAll: 'All',
      confidence: 'Confidence',
      confidenceMin: 'Min %',
      confidenceMax: 'Max %',
      dateRange: 'Date Range',
      datePlaceholder: 'Date range',
      adjustment: 'Adjustment',
      adjustmentAll: 'All',
      adjustmentWith: 'With adjustment',
      adjustmentWithout: 'Without adjustment',
      adjustmentPlaceholder: 'With or without adjustment',
      clear: 'Clear filters',
      clearAll: 'Clear all',
      apply: 'Apply',
      sourceMaskActive: 'Custom types',
    },
    sourceToggles: {
      aria: 'Source type',
      showLabel: 'Show: ',
      bankAccount: 'Bank account',
      creditCard: 'Credit card',
      checkingAccount: 'Checking account',
      cardShort: 'Card',
      investment: 'Investment',
      filterLabel: 'Source',
      lockedByAccount: 'Clear the selected account to filter by type.',
    },
    row: {
      empty: '—',
      credit: 'Credit',
      debit: 'Debit',
    },
    paymentMethods: {
      creditCard: 'Credit card',
      pix: 'Pix',
      ted: 'TED',
      doc: 'DOC',
      boleto: 'Boleto',
      card: 'Card',
      directDebit: 'Direct debit',
      directCredit: 'Direct credit',
      spei: 'SPEI',
      transfer: 'Transfer',
      fee: 'Fee',
      tax: 'Tax',
      unknown: 'Unknown',
    },
    status: {
      pending: 'Analyzing',
      matched: 'Match found',
      lowConfidenceSuggestion: 'Low confidence suggestion',
      reconciled: 'Reconciled',
      reconciledAuto: 'Reconciled (Auto)',
      reconciledRules: 'Reconciled (Rules)',
      reconciledHistorical: 'Reconciled (History)',
      reconciledAI: 'Reconciled (AI)',
      reconciledManual: 'Reconciled (Manual)',
      ignored: 'Ignored',
      needsReview: 'Needs Review',
    },
    internalOperation: {
      yield: 'Yield',
      rescue: 'Rescue',
      application: 'Application',
      other: 'Internal Op.',
      badge: 'Internal Op.',
    },
    badges: {
      transfer: 'Transfer',
      internalTransfer: 'Internal Transfer',
      investmentTransfer: 'Investment Transfer',
      fee: 'Fee',
      cardStatement: 'Card statement',
    },
    transferInfo: {
      fromAccount: 'From',
      toAccount: 'To',
      linkedAccount: 'Linked account',
    },
    linkedAccount: {
      adjustment: 'Adjustment',
      vendorAdvanceBalance: 'Vendor advance balance',
      bankTransaction: 'BANK TRANSACTION',
      linkedAccount: 'LINKED ACCOUNT',
      suggestedByAi: 'SUGGESTED BY AI',
      noMatch: 'No correspondence',
      awaitingAnalysis: 'Awaiting AI analysis or manual link',
      awaitingPairBadge: 'Awaiting pair',
      awaitingPairTitle: 'Awaiting matching leg',
      awaitingPairHint:
        'Internal transfer posted to "Funds in transit"; the counterpart will be linked once the matching transaction arrives in the statement.',
      link: 'Link',
      confirm: 'Confirm',
      reject: 'Reject',
      viewOtherBills: 'View other payables',
      alternateRejectDisabled: 'Reject applies to the primary suggestion',
      reconcileWith: 'Reconcile with this payable',
      alternateTypeBill: 'Accounts payable',
      alternateTypePayment: 'Payment',
      payment: 'Payment',
      transaction: 'Transaction',
    },
    confidence: {
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      auto: 'Auto',
      manual: 'Manual',
    },
    card: {
      search: 'Search',
      undo: 'Undo',
      parcela: 'installment',
      ajuste: 'adjustment',
      juros: 'interest',
    },
    cancelPosted: {
      title: 'Cancel reconciliation',
      description:
        'Journal entries will be reversed in the selected period and the reconciliation will be cancelled.',
      simpleDescription:
        'The reconciliation will be cancelled and the operational links will be reverted.',
      confirm: 'Confirm reversal and cancellation',
      postingsToReverse: 'Postings that will be reversed',
      reconciledPaymentsWarning:
        'This reconciliation also affects already reconciled payments. Cancelling it will revert those operational links together with the accounting reversal.',
      sharedImpactTitle: 'This reconciliation also affects {{count}} other item(s)',
      sharedImpactDescription:
        'Undoing the reconciliation reverts every item below, not only the payment you opened.',
      sharedImpactPayment: 'Payment of another bill',
      sharedImpactSameBillPayment: 'Another payment of this bill',
      sharedImpactBill: 'Bill',
      sharedImpactInvoice: 'Receivable invoice',
      refreshFailed:
        'Reconciliation undone, but the screen could not be refreshed. Reload the page to see the updated balance.',
    },
    actions: {
      reconcile: 'Reconcile',
      ignore: 'Ignore',
      unignore: 'Undo ignored',
      viewDetails: 'Edit transaction',
      viewReconciliation: 'View details',
      viewReconciliationDetails: 'Reconciliation Details',
      viewBill: 'View bill',
      viewInvoice: 'View invoice',
      refresh: 'Refresh',
      sync: 'Sync',
      import: 'Import',
      importOfx: 'Import OFX',
      importCard: 'Import Card',
      processingPending: 'Pending',
      bulkReconcile: 'Reconcile selected',
      bulkIgnore: 'Ignore',
      bulkEdit: 'Edit data',
      focusMode: 'Focus mode',
      retryPending: 'Reprocess',
      bulkCancel: 'Cancel',
      clearSelection: 'Clear selection',
    },
    bulkEdit: {
      title: 'Edit transaction data',
      description:
        'Change transaction type and/or payment method. Only bank transactions will be updated; card items will be skipped.',
      transactionType: 'Transaction type',
      transactionTypePlaceholder: "Don't change",
      paymentMethod: 'Payment method',
      paymentMethodPlaceholder: "Don't change",
      apply: 'Apply',
      noChanges: 'Select at least one field to change.',
    },
    groups: {
      noImport: 'No import',
      import: 'Import',
      importedAt: 'Imported at {{date}}',
      count: '{{count}} item',
      count_plural: '{{count}} items',
      loadMore: 'Load more',
    },
    table: {
      date: 'Date',
      description: 'Description',
      counterparty: 'Counterparty',
      amount: 'Amount',
      paymentMethod: 'Payment Method',
      account: 'Account',
      reconciledWith: 'Reconciled with',
      reconciledWithVenc: 'due',
      reconciledWithGlFailed: 'GL failed',
      status: 'Status',
      statusAndAi: 'Status / AI',
      confidence: 'Confidence',
      actions: 'Actions',
    },
    searchPlaceholder: 'Description, counterparty, reference, boleto, category...',
    bulkCount: '{{count}} item selected',
    bulkCount_plural: '{{count}} items selected',
    loading: 'Loading...',
    empty: 'No items found.',
    dateStart: 'Start date',
    dateEnd: 'End date',
    pagination: 'Page {{page}} of {{total}}',
    itemsPerPage: 'Items per page:',
    prevPage: 'Previous',
    nextPage: 'Next',
    balance: {
      currentLabel: 'Current balance:',
      updatedAt: 'on {{date}} at {{time}}',
    },
    counterparty: {
      search: 'Search counterparty',
      searchPlaceholder: 'Search by name or document...',
      searching: 'Searching...',
      notFound: 'No vendor found',
      notFoundVendor: 'No vendor found',
      notFoundCustomer: 'No customer found',
      notFoundBank: 'No bank account found',
      typeToSearch: 'Type to search',
      createNew: 'Create new vendor',
      createVendor: 'Create new vendor',
      createCustomer: 'Create new customer',
      createBankAccount: 'Create bank account',
    },
    messages: {
      loadError: 'Failed to load reconciliation items',
      refreshed: 'Items refreshed successfully',
      uploadSuccess: 'File uploaded successfully',
      uploadError: 'Failed to upload file',
      confirmIgnore: 'Do you want to ignore this transaction?',
      confirmIgnoreExplanation:
        'The transaction will be marked as ignored: it leaves pending queues and automatic suggestions. You can find it on the Ignored tab or re-enable it with Undo ignore when available.',
      confirmBulkIgnoreExplanation:
        '{{count}} selected transaction(s) will be marked as ignored: they leave pending queues and suggestions. You can find them on the Ignored tab or re-enable them later.',
      confirmIgnoreFocusExplanation:
        'The transaction will be marked as ignored and you will move to the next item. It leaves pending queues; find it on the Ignored tab or use Undo ignore to re-enable.',
      confirmUndo: 'Are you sure you want to undo the reconciliation of this transaction?',
      confirmUndoTitle: 'Undo reconciliation?',
      confirmUndoExplanation:
        'This action undoes the reconciliation and returns the item to analysis.',
      ignored: 'Transaction ignored',
      ignoreError: 'Failed to ignore',
      ignoreErrorCreatedTransferLeg:
        'This movement was created by a transfer reconciliation and cannot be ignored. To remove it, undo the reconciliation from the source movement.',
      ignoreErrorLiveGlPosting:
        'This reconciliation has live journals. Undo the reconciliation, with a reversal date and justification, before ignoring the movement.',
      unignored: 'Transaction returned to analysis status',
      unignoreError: 'Failed to undo ignored',
      undoSuccess: 'Reconciliation undone successfully',
      undoError: 'Failed to undo reconciliation',
      undoErrorJustificationRequired:
        'Enter a justification to reverse the accounting entries for this reconciliation.',
      undoErrorLiveGlPosting:
        'This reconciliation has active accounting entries. To undo it, reverse the entries by providing a reversal date and justification; the bill will stay approved if only the payment is undone.',
      undoErrorMovementJournalPosted:
        'This yield or investment tax (IR/IOF) is already booked on the bank movement itself, and undo does not reverse that journal. Reverse the yield/tax journal in the General Ledger first, then undo the reconciliation. Nothing was undone.',
      undoErrorCreatedBillNotDraft:
        'The bill this reconciliation created is no longer a draft (in approval, approved, accrued or paid). Resolve it in Accounts Payable before undoing.',
      undoErrorExportEvidenceSourcePeriodClosed:
        'The revenue source period is closed. Reopen it before undoing the reconciliation. The export evidence and assessment remain protected; nothing was undone.',
      undoErrorMismatchRemainderInUse:
        'The remaining amount this reconciliation scheduled was already sent to the bank or paid. Undo would delete that payment. Nothing was undone. If another bank line reconciled that amount, undo that reconciliation first. If the amount is at the bank, keep this reconciliation.',
      undoErrorMismatchPaymentNotRestorable:
        'This reconciliation replaced a payment that was confirmed or held by the bank, or the bill got another payment later. Undo cannot put the bill back as it was. Nothing was undone. If you added a payment later, delete it and try again. If not, keep this reconciliation.',
      undoErrorProviderRemovedPeriodClosed:
        'Reopen the {{month}} {{year}} accounting period to undo. The journal entry of this line, which the bank removed, is in that closed period. Nothing was undone.',
      undoDisabledClosedPeriod: 'Reconciliation cannot be edited in closed periods',
      bulkIgnoreSuccess: '{{count}} transaction(s) ignored',
      bulkIgnorePartial: '{{success}} success, {{failed}} failed',
      bulkIgnoreSweepMarked_one:
        '{{count}} line marked as automatic sweep was not ignored: unmark the line first.',
      bulkIgnoreSweepMarked_other:
        '{{count}} lines marked as automatic sweep were not ignored: unmark the lines first.',
      ignoreErrorSweepMarked:
        'This line is marked as automatic sweep. Unmark the line before ignoring it.',
      bulkIgnoreError: 'Failed to ignore transactions',
      retryPendingSuccess: '{{count}} items enqueued for reprocessing',
      retryPendingNone: 'No pending items found to reprocess',
      retryPendingError: 'Failed to reprocess pending items',
      bulkUpdateSuccess: '{{count}} transaction(s) updated',
      bulkUpdatePartial: '{{success}} updated, {{failed}} were not bank transactions',
      bulkUpdateError: 'Failed to update transactions',
      bulkReviewNoResolvedTypes:
        'Could not determine the type of all selected transactions. Refresh the list or select again.',
      actionNotImplemented: {
        ignore: '"Ignore" will be implemented',
        viewDetails: '"View details" will be implemented',
      },
      openWorkbenchToReconcile:
        'This suggestion type requires the reconciliation screen. Click on the card to open it.',
      reconcileError: 'Failed to reconcile',
      rejectSuggestionSuccess: 'Suggestion rejected',
      rejectSuggestionError: 'Failed to reject suggestion',
      rejectSuggestionNoCandidate: 'No suggestion found to reject.',
      rejectSuggestionCardOnly: 'Rejecting card suggestion requires the reconciliation screen.',
    },
    upload: {
      title: 'Import File',
      ofxTitle: 'Import OFX',
      cardTitle: 'Import Statement',
      documentTypeLabel: 'Document Type',
      documentTypeBank: 'Bank Statement',
      documentTypeCard: 'Card Statement',
      documentTypeBankDesc: 'OFX, CSV, CNAB.RET, CNAB.TXT',
      documentTypeCardDesc: 'CSV, PDF',
      documentTypeHint: 'Supported formats: {{formats}}',
      dropzoneTitle: 'Drag files here or click to select',
      dropzoneSubtitle: 'Supported formats: {{formats}}',
      selectFiles: 'Select Files',
      uploading: 'Uploading...',
      sendFiles: 'Send {{count}} file(s)',
      clear: 'Clear',
      maxFilesError: 'You can only select up to {{max}} files at a time.',
      filesRemoved: 'Some files were removed as they are not compatible with the selected type',
      uploadFilesError: 'Failed to upload {{count}} file(s): {{errors}}',
      duplicateFilesError: 'Duplicate document: {{files}}',
      duplicateFilesWarning: '{{count}} duplicate document(s): {{files}}',
      duplicateFilesUnknown: 'file(s)',
    },
  },
  statement: {
    title: 'Period statement',
    bankStatementHeader: 'Bank Statement',
    lancamentos: 'Transactions',
    period: 'Period',
    viewStatement: 'View statement',
    viewEnriched: 'View statement',
    viewEnrichedTooltip: 'View statement for the imported period',
    description: 'Transactions with reconciliation metadata',
    openingBalance: 'Opening balance',
    closingBalance: 'Closing balance',
    balanceContinuityWarningTitle: 'Warning: balance divergence',
    snapshotVsDailyTitle: 'External read vs daily balance',
    snapshotVsDailySummary: '{{count}} day where snapshot differs from stored daily balance.',
    snapshotVsDailySummary_plural:
      '{{count}} days where snapshot differs from stored daily balance.',
    transactionCount: '{{count}} transactions',
    loading: 'Loading statement...',
    empty: 'No transactions in period',
    noPeriod: 'Period not provided',
    timeout: 'Request timed out. Please try again.',
    error: 'Error loading statement',
    exportCsv: 'Export CSV',
    balanceAfter: 'Balance after',
    reconciledCounterparty: 'Counterparty',
    linkedDocument: 'Document',
  },
  periodHeader: {
    complete: 'Reconciled',
    statement: 'Statement',
    reconciled: 'Reconciled',
    pendingCount: 'Analyzing',
    matchedCount: 'Suggestions',
    reconciledCount: 'Reconciled',
    ignoredCount: 'Ignored',
    totalCount: 'All',
    difference: 'Difference',
    statements: 'Statements',
    statementsComplete: 'Statements complete',
    statementsPending: 'Pending statements',
    statementsPendingCount: '{{count}} pending',
    accounts: 'Accounts',
    cards: 'Cards',
    import: 'Import',
    synced: 'Synced',
    noControl: 'No control',
    legendComplete: 'Statements complete',
    legendPending: 'Pending statements',
    legendNone: 'No control',
  },
  cardItem: {
    details: 'Item Details',
    cardStatement: 'Card Statement',
    itemInfo: 'Item Information',
    merchant: 'Merchant',
    amount: 'Amount',
    date: 'Date',
    installment: 'Installment',
    status: 'Status',
    description: 'Description',
    matchCandidates: 'Match Suggestions',
    noCandidates: 'No suggestions found',
    ignore: 'Ignore',
    confirmIgnoreDescription:
      'This statement line will be marked as ignored: it will no longer appear in match suggestions. You can find it on the Ignored tab or re-enable it with Undo ignore when available.',
    confirmSuccess: 'Item reconciled successfully',
    confirmError: 'Error reconciling item',
    ignoreSuccess: 'Item ignored',
    ignoreError: 'Error ignoring item',
    createBill: 'Create Bill',
    createBillSuccess: 'Bill created and reconciled successfully',
    createBillError: 'Error creating bill',
  },
  smartUpload: {
    title: 'Smart Upload',
    dropzoneTitle: 'Smart Upload',
    dropzoneSubtitle:
      'Drop statement or invoice files. AI will automatically identify the type and account.',
    selectFiles: 'Select Files',
    clear: 'Clear',
    analyzeFiles: 'Analyze Files',
    analyzing: 'Analyzing files...',
    analyzingDesc: 'AI is identifying the document type and extracting information.',
    back: 'Back',
    import: 'Import Files',
    importing: 'Importing files...',
    importingDesc: 'Processing and saving transactions.',
    saveProfiles: 'Save preferences for future imports',
    confidence: 'Confidence',
    detected: 'Detected',
    suggestedAccount: 'Suggested account',
    changeAccount: 'Change account',
    existingProfile: 'Existing import profile found',
    existingProfileShort: 'Profile saved',
    bankStatement: 'Bank Statement',
    bankStatementShort: 'Statement',
    cardStatement: 'Card Statement',
    cardStatementShort: 'Card',
    documentTypeLabel: 'Document type',
    documentTypeHint:
      'Override if AI misclassified: bank account statement vs card invoice/transaction list.',
    noCardNumberInFile:
      'No card number (last digits) was found in the file. Select the card manually.',
    transactions: 'transactions',
    transactionsShort: 'trans.',
    selectBankAccount: 'Select account',
    expandCards: 'Expand',
    collapseCards: 'Collapse',
    removeFile: 'Remove file',
    kaminoIntegrationLink: 'Open Kamino integration',
    openFinanceIntegrationLink: 'Open Open Finance (integrations)',
    statusReady: 'Ready',
    statusPending: 'Pending',
    statusActionRequired: 'Action required',
    statusError: 'Error',
    accountNotFound: 'Account not found',
    uncertaintyMessage:
      'Could not identify the account with certainty. Select an existing account or create a new one to link.',
    selectExistingAccount: 'Or select another account:',
    selectAccountOrCreate: 'Select an account or create a new one:',
    accountNotFoundDetails:
      '{{bankName}} • Bank: {{bankCode}} • Branch: {{branch}} • Account: {{account}}',
    didYouMean: 'Did you mean?',
    useThis: 'Use this',
    changeAccountShort: 'Change',
    accountDisplayBank: 'Bank',
    accountDisplayChecking: 'Checking',
    accountDisplaySavings: 'Savings',
    accountDisplaySalary: 'Salary',
    accountDisplayPayment: 'Payment',
    accountDisplayInvestment: 'Investment',
    accountDisplayBenefits: 'Benefits',
    filesReadySummary: '{{ready}} of {{total}} files ready to import',
    transactionsCount: '{{count}} transactions',
    warnings: {
      encoding: 'Encoding detected as {{encoding}}',
      dateFormat: 'Unusual date format detected',
    },
    errors: {
      analysisNotFound: 'Analysis not found',
      unknownError: 'Unknown error',
    },
    bankStatementPdfNotSupported:
      'Bank statement PDFs are not supported. Use OFX or CSV format to import your statement.',
    cardSelectionRequired: 'Select at least one card for each invoice file before importing.',
    quickAdd: {
      bankTitle: 'Quick Add - Bank Account',
      cardTitle: 'Quick Add - Credit Card',
      addBank: 'Add new account',
      addBankShort: 'New account',
      createNewBankAccount: 'Create new bank account',
      addCard: 'Add new card',
      addCardShort: 'New card',
      createBank: 'Create Account',
      createCard: 'Create Card',
      cancel: 'Cancel',
      entity: 'Entity',
      accountName: 'Account Name',
      accountType: 'Account Type',
      accountTypeChecking: 'Checking',
      accountTypeSavings: 'Savings',
      accountTypeSalary: 'Salary',
      accountTypePayment: 'Payment',
      accountTypeInvestment: 'Investment',
      accountTypeBenefits: 'Benefits',
      bankCode: 'Bank Code',
      bankName: 'Bank Name',
      bankSearch: 'Search by name or code...',
      detectedBankNameHint: 'Detected in the file: {{bankName}}. Select the bank above.',
      providerName: 'Provider Name',
      platformName: 'Platform Name',
      branchCode: 'Branch',
      accountNumber: 'Account Number',
      cardName: 'Card Name',
      brand: 'Brand',
      lastFourDigits: 'Last 4 digits',
      cardholderName: 'Cardholder',
      closingDay: 'Closing Day',
      dueDay: 'Due Day',
      successBank: 'Account created successfully!',
      successCard: 'Card created successfully!',
      errorBank: 'Error creating bank account',
      errorCard: 'Error creating card',
      requiredFields: 'Fill in all required fields',
      duplicateBankAccount:
        'Bank account with same bank, branch and account number already exists ({{name}}). Select it from the list.',
    },
  },
  bankAccount: {
    notFound: 'Bank account not found',
    loadError: 'Failed to load bank account',
    importPending: 'Import received. Waiting for processing...',
    importProcessing: 'Import in progress...',
    importCompleted: 'Import completed. Transactions were updated.',
    importCancelled: 'The import was cancelled.',
    importFailed: 'The last import failed.',
    importStuck: 'The import is taking longer than expected.',
    tryAgain: 'Try again',
    reprocessSuccess: 'Import re-queued. Processing will resume shortly.',
    reprocessNoDocuments: 'No documents found to reprocess.',
    reprocessError: 'Error retrying. Please try again later.',
    reprocessCompleted: 'Import missing transactions',
    reprocessCompletedHint:
      'Re-importing the same file does not add new transactions by default. Use the button below to reprocess already imported statements and add missing transactions.',
    reprocessCompletedSuccess:
      'Statements re-queued. Missing transactions will be imported shortly.',
    reprocessCompletedNoDocuments: 'No completed statements found to reprocess.',
    meta: {
      currentBalance: 'Current balance',
      currentBalanceTooltip: 'Updated on {{date}}',
      currentBalanceUnknownDate: 'unknown date',
      bank: 'Bank',
      branch: 'Branch',
      account: 'Account',
      type: 'Type',
      entity: 'Entity',
      copyBranch: 'Copy branch number',
      copyBranchSuccess: 'Branch number copied',
      copyAccount: 'Copy account number',
      copyAccountSuccess: 'Account number copied',
      copyError: 'Failed to copy',
    },
    openFinance: {
      unlink: 'Unlink',
      unlinkComingSoon: 'Unlink — Coming soon',
      unlinkComingSoonTooltip: 'Open Finance unlink will be available soon.',
      unlinkSuccess: 'Account unlinked successfully.',
      unlinkError: 'Failed to unlink account. Please try again.',
      unlinkDialogTitle: 'Confirm unlink',
      unlinkDialogDescription:
        'The Open Finance connection will be deactivated. This account will no longer receive automatic synchronization. Continue?',
      unlinkDialogChecking:
        'Checking whether this connection is used by other accounts or cards...',
      unlinkDialogCheckError:
        'Could not verify whether this connection is shared. Close and try again.',
      unlinkDialogDescriptionShared:
        'This Open Finance connection is also used by {{parts}}. It will be deactivated for all of them, and none will receive automatic synchronization anymore.',
      unlinkDialogSharedAccountsPart_one: '{{count}} other bank account',
      unlinkDialogSharedAccountsPart_other: '{{count}} other bank accounts',
      unlinkDialogSharedCardsPart_one: '{{count}} credit card',
      unlinkDialogSharedCardsPart_other: '{{count}} credit cards',
      unlinkDialogSharedJoiner: ' and ',
      unlinkDialogAcknowledge: 'I understand this also disconnects the accounts/cards above.',
    },
    tabs: {
      overview: 'Overview',
      statement: 'Statement',
      internalOps: 'Internal Operations',
      transfers: 'Transfers',
    },
    statement: {
      empty: 'No transactions in period',
      loadError: 'Failed to load statement',
      balancesOnlyBanner:
        'You only have access to balances for this account. The statement shows the aggregated end-of-day balance, without individual transactions.',
      balancesOnlyEmpty: 'No daily balances in period',
      periodClearedEmptyTitle: 'No period selected',
      periodClearedEmptyDescription: "Select a period to view this account's statement.",
      periodClearedEmptyAction: 'Select period',
      loadingBalances: 'Loading balances...',
      loadingTransactions: 'Loading statement...',
      warningsReasonPending_one:
        '{{count}} pending entry, still excluded from the balance and reconciliation',
      warningsReasonPending_other:
        '{{count}} pending entries, still excluded from the balance and reconciliation',
      warningsReasonBalanceMismatch: 'balance out of sync',
      warningsReasonPendingUnverified: "couldn't confirm whether there are recent pending items",
      warningsReasonsJoiner: 'and',
      warningsSyncHint: 'Sync from the (⋮) menu to update.',
      warningsFileOnlyHint: 'Check the balance reported in the imported file.',
      clearFilters: 'Clear filters',
      amountLabel: 'Transaction amount',
      minAmount: 'Min',
      maxAmount: 'Max',
      itemCount: '{{count}} transactions',
      itemCountLoaded: '{{loaded}} of {{total}} transactions',
      loadingMore: 'Loading more transactions...',
      loadMore: 'Load more',
      allLoaded: 'All transactions in the period are loaded',
      periodCustom: 'Custom',
      periodThisMonth: 'This month',
      sectionTitle: 'Statement',
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
      expandDateGroup: 'Expand transactions for {{date}}',
      collapseDateGroup: 'Collapse transactions for {{date}}',
      filtersButtonLabel: 'Filters',
      activeFiltersLabel: 'Active filters:',
      filterChipSearch: 'Search',
      filterChipType: 'Type',
      filterChipPeriod: 'Period',
      filterChipMinAmount: 'Min amount',
      filterChipMaxAmount: 'Max amount',
      typeFilterLabel: 'Transaction type',
      periodFilterLabel: 'Period',
      periodFrom: 'From',
      periodTo: 'To',
      filters: {
        all: 'All',
      },
      table: {
        date: 'Date',
        balance: 'Balance at end of day',
        transactionCount: 'Transactions',
        time: 'Time',
        description: 'Description',
        type: 'Type',
        amount: 'Amount',
        balanceAfter: 'Balance after',
      },
      noTransactionsForDay: 'No transactions on this day',
      transactionTypeLabels: {
        INTERNAL_TRANSFER: 'Transfer',
        INTERNAL_OP: 'Internal',
        FEE_TAX: 'Fee',
        VENDOR_PAYMENT: 'Payment',
        CUSTOMER_RECEIPT: 'Receipt',
        EMPLOYEE_PAYMENT: 'Payroll',
        OTHER: 'Other',
      },
      summaryCards: {
        balance: 'Balance',
        connection: 'Bank connection',
        connectedOpenFinance: 'Connected via Open Finance',
        connectedKamino: 'Connected via Kamino',
        connectedPlaid: 'Connected via Plaid',
        notConnected: 'Not connected',
        connectionTooltip:
          'Syncs automatically (near real-time, with periodic backfills). Balance and statement can take a moment to converge.',
        pending: 'Pending',
        pendingTooltip:
          "Counts only the period and transaction type selected in the statement below — not the account's total pending count.",
      },
      manageConnectionHint: 'Managed in',
      manageConnectionLinkLabel: 'Integrations',
      unlinkTooltip: "Removes this account's bank connection.",
      recalculateDailyBalance: 'Recalculate daily balance',
      syncPendingTransactions: 'Sync pending',
      syncPendingTransactionsRunning: 'Syncing...',
      syncPendingTransactionsTooltip:
        'Fetches new transactions since the last sync (incremental). Does not replay full account history.',
      syncPendingThenRecalculateTooltip:
        'Fetches new transactions (incremental), then recalculates the end-of-day balance for each day in the selected statement period.',
      syncPendingTransactionsSuccess: 'Sync enqueued. Transactions will update shortly.',
      syncPendingTransactionsError: 'Sync failed. Please try again.',
      syncPlaidSuccess: 'Sync finished.',
      syncPlaidNotReady: 'The connection is still being set up. Try again in a moment.',
      periodRequiredForAction: 'Select a period before continuing.',
      integrationPermissionRequired: "You don't have permission to manage this integration.",
      syncBannerFetchingTitle: 'Fetching transactions from the bank...',
      syncBannerWaitingTitle: 'Waiting for transactions to be processed...',
      syncBannerFetchingBodyOpenFinance:
        'We are fetching data from Open Finance. Do not reload the page.',
      syncBannerFetchingBodyKamino: 'We are fetching data from Kamino. Do not reload the page.',
      syncBannerFetchingBodyGeneric: 'Syncing with the integration. Do not reload the page.',
      syncBannerWaitingBody:
        'Transactions have been downloaded and are being saved. The statement will refresh automatically in a moment.',
      recalculating: 'Recalculating...',
      recalculateSuccess: 'Daily balance recalculated successfully',
      recalculateSuccessReverse: 'Balance recalculated from the bank snapshot',
      recalculateSuccessForward:
        'Balance recalculated by summing transactions from initial balance',
      recalculateDailyBalanceTooltipSnapshot:
        'Rewrites daily balances anchored on the latest bank snapshot, walking backward in time. Also updates the "Last balance".',
      recalculateDailyBalanceTooltipForward:
        'Sums all transactions from the initial balance. Without an external snapshot, historical drift may appear in the current balance.',
      recalculatePartialSuccess:
        'Daily balance recalculated ({{filled}} filled, {{failed}} failed)',
      recalculateError: 'Error recalculating daily balance',
      initialBalanceAlertTitle: 'Initial balance not configured',
      initialBalanceAlertDescription:
        'For better daily balance calculation results, fill in the initial balance value and initial date in the account edit form.',
      initialBalanceAlertEdit: 'Go to edit',
      initialBalanceAlertProceed: 'Recalculate anyway',
      balanceHealthTitle: 'Consolidated balance analysis',
      lastBalanceVsStatementTitle: 'Account balance and statement are out of step',
      lastBalanceVsStatementBody:
        'Current balance (Overview): {{card}}. In the statement, end-of-day balance for the most recent day in this period ({{date}}): {{statement}}. Difference: {{diff}}. This is common in Open Finance: the balance can refresh before the transaction appears in the list — use "Sync pending" (syncs and recalculates the statement), or wait for Open Finance to deliver the transactions.',
      snapshotVsTransactionsTitle: 'Current balance differs from imported transactions',
      snapshotVsTransactionsBody:
        'Current balance (Open Finance): {{snapshot}}. Based on imported transactions through {{lastTxDate}}, the expected balance would be {{implied}}. Difference: {{diff}}. The balance often updates before all transactions arrive — wait for the next sync or use "Sync pending".',
      kindDeclaredBadge: 'Declared',
      kindDeclaredTooltip: 'Authoritative balance from {{source}}',
      balanceSourceLabels: {
        OPEN_FINANCE: 'Open Finance',
        RECONCILIATION: 'Reconciliation',
        STATEMENT_IMPORT: 'Statement import',
        MANUAL: 'Manual entry',
      },
      calculationDirectionLabels: {
        FORWARD: 'Projected forward',
        BACKWARD: 'Projected backward',
        ANCHORED: 'Anchored',
      },
      snapshotDiffAria: 'Balance reported by the bank differs from the calculated balance',
      snapshotTooltipTitle: 'Daily balance comparison',
      snapshotTooltipSource: 'Open Finance',
      calculatedBalanceLabel: 'Calculated from transactions',
      snapshotTooltipDiffLabel: 'Difference',
      balanceStatusVerifiedBadge: 'Verified',
      balanceStatusVerifiedTooltip:
        'This balance was confirmed by the bank through Open Finance for this day.',
      balanceStatusProjectedBadge: 'Projected',
      balanceStatusProjectedTooltip:
        'Balance calculated from imported transactions. The bank has not confirmed this day through Open Finance yet.',
      balanceStatusDivergentTooltip:
        'A transaction may not have arrived through Open Finance yet or may have been duplicated.',
    },
    actions: {
      edit: 'Edit',
    },
    overview: {
      bank: 'Bank',
      account: 'Account',
      balance: 'Last Balance',
      details: 'Account Details',
      lastImport: 'Last import',
      balanceUpdatedAt: 'Updated at {{date}}',
      balanceDivergenceTitle: 'Balance divergence',
      balanceDivergenceBody:
        'The {{source}} snapshot ({{snapshot}}) differs from the known balance ({{lastKnown}}) by {{diff}}.',
      balanceDivergenceRebaseHint:
        'On the Statement tab: Open Finance accounts — "Sync pending" (sync + recalculate); others — "Recalculate daily balance". With a bank snapshot, balances re-anchor and the "Last balance" updates, clearing this alert.',
    },
    internalOps: {
      title: 'Internal Operations',
      empty: 'No internal operations found',
      total: '{{count}} operations',
      unknownPairedAccount: 'Not identified',
      summary: {
        yield: 'Yields',
        rescue: 'Rescues',
        application: 'Applications',
        netImpact: 'Balance Impact',
      },
      types: {
        yield: 'Yield',
        rescue: 'Rescue',
        application: 'Application',
        other: 'Other',
      },
      filters: {
        allTypes: 'All types',
        dateRange: 'Select period',
      },
      table: {
        date: 'Date',
        description: 'Description',
        type: 'Type',
        amount: 'Amount',
        pairedAccount: 'Source/Destination Account',
        status: 'Status',
      },
      status: {
        reconciled: 'Reconciled',
        pending: 'Pending',
      },
      pagination: {
        showing: 'Showing {{from}}-{{to}} of {{total}}',
        previous: 'Previous',
        next: 'Next',
      },
    },
    transfers: {
      title: 'Transfers',
      empty: 'No transfers found',
      total: '{{count}} transfers',
      unknownCounterparty: 'Not identified',
      summary: {
        sent: 'Sent',
        received: 'Received',
        netTransfer: 'Net Balance',
        sentCount: '{{count}} transfers',
        receivedCount: '{{count}} transfers',
      },
      direction: {
        sent: 'Sent',
        received: 'Received',
      },
      filters: {
        allDirections: 'All directions',
        allCounterparties: 'All accounts',
        dateRange: 'Select period',
      },
      table: {
        date: 'Date',
        description: 'Description',
        direction: 'Direction',
        amount: 'Amount',
        method: 'Method',
        counterparty: 'Source/Destination Account',
        status: 'Status',
      },
      status: {
        reconciled: 'Reconciled',
        pending: 'Pending',
      },
      pagination: {
        showing: 'Showing {{from}}-{{to}} of {{total}}',
        previous: 'Previous',
        next: 'Next',
      },
    },
  },
  creditCard: {
    notFound: 'Credit card not found',
    childCards: {
      usedCount_one: '{{count}} card identified in history',
      usedCount_other: '{{count}} cards identified in history',
    },
    brands: {
      VISA: 'Visa',
      MASTERCARD: 'Mastercard',
      AMEX: 'American Express',
      ELO: 'Elo',
      HIPERCARD: 'Hipercard',
      DINERS: 'Diners Club',
      OTHER: 'Other',
    },
    status: {
      ACTIVE: 'Active',
      INACTIVE: 'Inactive',
      CANCELLED: 'Cancelled',
      EXPIRED: 'Expired',
    },
    metadata: {
      final: 'ending',
      closingDay: 'Closes on day {{day}}',
      dueDay: 'Due on day {{day}}',
    },
    tabs: {
      overview: 'Overview',
      statements: 'Statements',
    },
    actions: {
      edit: 'Edit',
    },
    overview: {
      brand: 'Brand',
      titular: 'Cardholder',
      validity: 'Validity',
      limit: 'Limit',
      available: 'Available',
      details: 'Card Details',
      name: 'Name',
      description: 'Description',
      entity: 'Entity',
      closingDay: 'Closing day',
      dueDay: 'Due day',
      isDefault: 'Default card',
      openFinance: 'Open Finance Connection',
      openFinanceDescription:
        'This card is linked to Open Finance Brasil for automatic statement synchronization.',
    },
    statements: {
      periodLabel: 'Period',
      periodCustom: 'Custom',
      searchPlaceholder: 'Search description...',
      clearFilters: 'Clear filters',
      itemCount: '{{count}} transactions',
      noItemsFound: 'No transactions found.',
      emptyTime: '—',
      statusLabel: 'Status',
      loadError: 'Failed to load statements',
      itemsLoadError: 'Failed to load statement items',
      collapseInvoice: 'Collapse statement',
      expandInvoice: 'Expand statement',
      collapseTransactionsForDate: 'Collapse transactions for {{date}}',
      expandTransactionsForDate: 'Expand transactions for {{date}}',
      invoiceSummary: 'Consolidated statement summary',
      informedTotal: 'Reported total',
      calculatedTotal: 'Calculated total',
      divergence: 'Difference',
      settlement: 'Settlement',
      paidAmount: 'Paid',
      providerPaymentEvidence: 'Pluggy payment evidence',
      estimatedClosing: 'Estimated',
      incompleteAccess: 'The summary excludes cards you cannot access.',
      subtotalsByCard: 'Subtotals by card',
      settlementStatus: {
        unlinked: 'Not linked to accounts payable',
        closed: 'Closed, awaiting payment',
        partial: 'Partially paid',
        paid: 'Paid',
      },
      itemKind: {
        purchase: 'Purchase',
        refund: 'Refund',
        statement_payment: 'Statement payment',
        unknown: 'Review type',
      },
      retry: 'Retry',
      empty: 'No statements in period',
      noItems: 'No transactions in this statement',
      noCardAccount:
        'This card has no linked statements. Connect via Open Finance or import statements to view transactions.',
      periods: {
        last5: 'Last 5 days',
        last30: 'Last 30 days',
        thisMonth: 'This month',
        lastMonth: 'Last month',
        last90: 'Last 90 days',
      },
      status: {
        all: 'All',
        open: 'Open',
        closed: 'Closed',
        partial: 'Partial',
        paid: 'Paid',
      },
      table: {
        closingDate: 'Closing',
        dueDate: 'Due',
        total: 'Total',
        status: 'Status',
        date: 'Date',
        transactionCount: 'Transactions',
        childCard: 'Card',
        merchant: 'Merchant',
        amount: 'Amount',
      },
    },
  },
};
